<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownHeader from 'shared/components/ui/dropdown/DropdownHeader.vue';
import WootDropdownDivider from 'shared/components/ui/dropdown/DropdownDivider.vue';
import AvailabilityStatusBadge from '../widgets/conversation/AvailabilityStatusBadge.vue';
import wootConstants from 'dashboard/constants/globals';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default {
  components: {
    WootDropdownHeader,
    WootDropdownDivider,
    WootDropdownMenu,
    WootDropdownItem,
    AvailabilityStatusBadge,
  },
  data() {
    return {
      isStatusMenuOpened: false,
      isBusyMenuOpened: false,
      isUpdating: false,
      busyStatusOptions: [], // Dynamically fetched from API
    };
  },

  computed: {
    ...mapGetters({
      getCurrentUserAvailability: 'getCurrentUserAvailability',
      currentAccountId: 'getCurrentAccountId',
      currentUserAutoOffline: 'getCurrentUserAutoOffline',
      currentUserAvailabilityReason: 'getCurrentUserAvailabilityReason',
    }),
    availabilityDisplayLabel() {
      const availabilityIndex = AVAILABILITY_STATUS_KEYS.findIndex(
        key => key === this.currentUserAvailability
      );
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST')[
        availabilityIndex
      ];
    },
    currentUserAvailability() {
      return this.getCurrentUserAvailability;
    },
    availabilityStatuses() {
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST').map(
        (statusLabel, index) => ({
          label: statusLabel,
          value: AVAILABILITY_STATUS_KEYS[index],
          disabled:
            this.currentUserAvailability === AVAILABILITY_STATUS_KEYS[index],
        })
      );
    },
    hasBusyReasons() {
      return this.busyStatusOptions.length > 0;
    },
  },

  async mounted() {
    await this.fetchAvailabilityReasons();
  },

  methods: {
    async fetchAvailabilityReasons() {
      try {
        await this.$store.dispatch(
          'availability/getAvailability',
          this.currentAccountId
        );

        const availabilityReasons =
          this.$store.getters['availability/getAvailabilityReasons'];

        if (!availabilityReasons) {
          throw new Error('No availability reasons found');
        }

        this.busyStatusOptions = availabilityReasons.map(reason => ({
          id: reason.id,
          label: `${reason.emoji} ${reason.reason}`,
          value: reason.reason.toLowerCase().replace(/\s+/g, '_'),
          color: reason.color,
        }));
      } catch (error) {
        useAlert('Failed to fetch availability reasons. Please try again.');
      }
    },
    getCurrentUserAvailabilityReasonLabel(id) {
      const reason = this.busyStatusOptions.find(option => option.id == id);
      return reason ? reason.label : '';
    },
    openStatusMenu() {
      this.isStatusMenuOpened = true;
    },
    closeStatusMenu() {
      this.isStatusMenuOpened = false;
    },
    openBusyMenu() {
      if (this.hasBusyReasons) {
        this.isBusyMenuOpened = true;
      }
    },
    closeBusyMenu() {
      this.isBusyMenuOpened = false;
    },
    updateAutoOffline(autoOffline) {
      this.$store.dispatch('updateAutoOffline', {
        accountId: this.currentAccountId,
        autoOffline,
      });
    },
    changeAvailabilityStatus(availability, availability_reason_id = null) {
      if (this.isUpdating) {
        return;
      }
      this.isUpdating = true;
      try {
        this.$store.dispatch('updateAvailability', {
          availability,
          availability_reason_id,
          account_id: this.currentAccountId,
        });
      } catch (error) {
        useAlert(
          this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR')
        );
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>

<template>
  <WootDropdownMenu>
    <WootDropdownHeader :title="$t('SIDEBAR.SET_AVAILABILITY_TITLE')" />

    <WootDropdownItem
      v-for="status in availabilityStatuses"
      :key="status.value"
      class="flex items-baseline"
    >
      <woot-button
        v-if="status.value === 'online' || status.value === 'offline'"
        size="small"
        :color-scheme="status.disabled ? '' : 'secondary'"
        :variant="status.disabled ? 'smooth' : 'clear'"
        class-names="status-change--dropdown-button"
        @click="changeAvailabilityStatus(status.value)"
      >
        <AvailabilityStatusBadge :status="status.value" />
        {{ status.label }}
      </woot-button>

      <woot-button
        v-if="
          status.value === 'busy' &&
          !hasBusyReasons &&
          !currentUserAvailabilityReason
        "
        size="small"
        :color-scheme="status.disabled ? '' : 'secondary'"
        :variant="status.disabled ? 'smooth' : 'clear'"
        class-names="status-change--dropdown-button"
        @click="changeAvailabilityStatus(status.value)"
      >
        <AvailabilityStatusBadge :status="status.value" />
        {{ status.label }}
      </woot-button>

      <!-- BUSY WITH SUBMENU -->
      <div
        v-if="
          status.value === 'busy' &&
          (hasBusyReasons || currentUserAvailabilityReason)
        "
        :class="[
          // eslint-disable-next-line vue/prefer-separate-static-class
          'status-change--dropdown-button relative inline-flex items-center justify-between w-full h-8 pr-2.5 pl-2 text-xs font-medium rounded-full cursor-pointer transition-colors duration-200',
          currentUserAvailabilityReason
            ? 'bg-[hsl(24,100%,96.08%)] dark:bg-[hsl(23.19,63.64%,36.67%)] dark:hover:bg-[hsl(23,63.83%,18.43%)] text-[hsl(23.4,63.86,48.82%)] dark:text-white dark:hover:text-slate-100'
            : 'bg-transparent dark:bg-transparent hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-100 hover:text-slate-800 dark:hover:text-slate-100',
        ]"
        role="button"
        tabindex="0"
        @mouseenter="openBusyMenu"
        @mouseleave="closeBusyMenu"
      >
        <!-- BUSY w/ reason -->
        <div v-if="currentUserAvailabilityReason" class="w-full">
          <div class="w-full flex items-center justify-between">
            <div class="flex items-center gap-1 truncate">
              <span>{{
                !hasBusyReasons
                  ? `${currentUserAvailabilityReason.emoji} ${currentUserAvailabilityReason.reason}`
                  : getCurrentUserAvailabilityReasonLabel(
                      currentUserAvailabilityReason.id
                    )
              }}</span>
            </div>
            <span>{{ '>' }}</span>
          </div>
        </div>

        <!-- BUSY w/o reason -->
        <div v-if="!currentUserAvailabilityReason" class="w-full">
          <div class="w-full flex items-center justify-between">
            <div class="flex items-center gap-1 truncate ml-[0.125rem]">
              <AvailabilityStatusBadge status="busy" />
              <span>{{ status.label }}</span>
            </div>
            <span v-if="hasBusyReasons">{{ '>' }}</span>
          </div>
        </div>

        <!-- BUSY SUBMENU -->
        <div
          v-if="isBusyMenuOpened && hasBusyReasons"
          class="absolute left-[100%] ml-[-5rem] top-0"
        >
          <div
            class="min-w-[12rem] ml-[5.75rem] px-2 bg-white dark:bg-slate-800 dark:border dark:border-slate-600 shadow-md rounded-lg p-2 max-h-[21.95rem] overflow-y-auto [scrollbar-width:none]"
          >
            <woot-button
              v-for="option in busyStatusOptions"
              :key="option.id"
              size="small"
              :color-scheme="
                currentUserAvailabilityReason &&
                currentUserAvailabilityReason.id === option.id
                  ? 'primary'
                  : 'secondary'
              "
              :variant="
                currentUserAvailabilityReason &&
                currentUserAvailabilityReason.id === option.id
                  ? 'smooth'
                  : 'clear'
              "
              class-names="status-change--dropdown-button my-1"
              @click="changeAvailabilityStatus('busy', option.id)"
            >
              {{ option.label }}
            </woot-button>
          </div>
        </div>
      </div>
    </WootDropdownItem>

    <WootDropdownDivider />

    <!-- AUTO OFFLINE TOGGLE -->
    <WootDropdownItem class="flex items-center justify-between p-2 m-0">
      <div class="flex items-center">
        <fluent-icon
          v-tooltip.right-start="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_TEXT')"
          icon="info"
          size="14"
          class="mt-px"
        />
        <span
          class="mx-1 my-0 text-xs font-medium text-slate-600 dark:text-slate-100"
        >
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
        </span>
      </div>
      <woot-switch
        size="small"
        class="mx-1 mt-px mb-0"
        :value="currentUserAutoOffline"
        @input="updateAutoOffline"
      />
    </WootDropdownItem>

    <WootDropdownDivider />
  </WootDropdownMenu>
</template>
